import React from 'react';
import { connect } from 'react-redux';

import {
  Card,
  CardContent,
  Button,
  FormControl,
  TextField,
  CircularProgress,
  InputAdornment,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert, AlertTitle } from '@material-ui/lab';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import IconButton from '@material-ui/core/IconButton';

import splashScreen from '../assets/images/login-splash.jpg';
import IconRms from '../assets/images/icon-RMS.svg';
import ptsLogo from '../assets/images/PTS-logo.png';

import { notify } from 'reducers/NotifierReducer';
import { useLocation, Redirect } from 'react-router';
import { resetPass, submitLoginForm } from '../reducers/UserReducer';
import { passValidationErrors } from 'utils/functions';

const useStyles = makeStyles((theme) => ({
  pageWrap: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundImage: `url(${splashScreen})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  card: {
    width: 480,
    maxWidth: '95%',
    background: 'fff',
    '& img': {
      width: '100%',
    },
  },
  content: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
  branding: {
    width: '100%',
    height: 200,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: '#0051a0',
    '& img': {
      width: 150,
      height: 'auto',
    },
  },
  branding2: {
    width: '100%',
    textAlign: 'center',
    padding: '25px 0 5px',
    '& img': {
      height: 50,
      width: 'auto',
    },
  },
  action: {
    textAlign: 'center',
    marginTop: 30,
    justifyContent: 'center',
    '& .btn': {
      background: '#0051a0',
      color: '#fff',
      '&:hover': {
        background: '#021090',
      },
    },
    '& button': {
      paddingLeft: '4em',
      paddingRight: '4em',
      textTransform: 'uppercase',
    },
    '& button.Mui-disabled': {
      color: 'rgba(0, 0, 0, 0.26)',
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
    },
  },

  passReset: {
    textAlign: 'right',
    '& span': {
      fontSize: 13,
      color: '#808080',
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
  version: {
    position: 'absolute',
    bottom: '0.5em',
    left: '1em',
    lineHeight: 1,
  },
  invalid: {
    textAlign: 'center',
    fontSize: '1.5em',
    margin: '2em 0',
  },
  alert: {
    marginBottom: 32,
    '&.MuiAlert-standardError': {
      color: 'rgb(84, 18, 18)',
      backgroundColor: 'rgb(250, 234, 234)',
    },
  },
}));

const PageResetPassword = (props) => {
  const classes = useStyles();
  const { appVersion } = props.appInfo;
  const { user, code } = useLocation().state || {};
  const [username, setUsername] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [newPass, setNewPass] = React.useState('');
  const [confirmNewPass, setConfirmNewPass] = React.useState('');
  const [error, setError] = React.useState(false);
  const [errors, setErrors] = React.useState([]);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const disabled = Boolean(error || !newPass || loading || newPass !== confirmNewPass);
  const repeatPassErr = newPass === confirmNewPass ? false : "Passwords don't match";
  const [goToLogin, setGoToLogin] = React.useState(false);
  const [goToHome, setGoToHome] = React.useState(false);

  React.useEffect(() => {
    if (!user) {
      setGoToLogin(true);
      return;
    }
    setUsername(user);
  }, [user]);

  React.useEffect(() => {
    const validationErrors = passValidationErrors('', newPass);
    if (newPass && validationErrors.length) {
      setError(validationErrors[0]);
    } else {
      if (error) setError(false);
    }
    // eslint-disable-next-line
  }, [newPass]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const resetPassword = async () => {
    setLoading(true);
    try {
      let result = await resetPass(code, newPass, username);
      props.notify('Password updated!', 'success');
      if (result === 'TFA') {
        setGoToLogin(true);
      } else {
        await props.submitLoginForm(username, newPass);
        setGoToHome(true);
      }
    } catch (err) {
      if (err.errors && err.errors.length) {
        setErrors(err.errors);
      } else {
        const message = err.message ? err.message : 'Error resetting password';
        setErrors([message]);
      }
    }
    setLoading(false);
  };

  const redirectToLogin = async () => {
    setGoToLogin(true);
  };

  const handleNewPasswordChange = (event) => {
    setNewPass(event.target.value);
  };

  const handleConfirmPasswordChange = (event) => {
    setConfirmNewPass(event.target.value);
  };

  const renderPasswordRestForm = () => {
    return (
      <>
        <div style={{ position: 'relative' }}>
          <div className="mb-3">
            <FormControl size="small" className="w-100">
              <TextField
                className={classes.textField}
                label="Username"
                value={username}
                onChange={(ev, val) => setUsername(val)}
                required
                size="small"
                variant="outlined"
                placeholder="Enter your username"
                type="text"
                disabled={true}
              />
            </FormControl>
          </div>
          <div className="mb-3">
            <FormControl size="small" className="w-100">
              <TextField
                autoComplete="hidden"
                size="small"
                required
                variant="outlined"
                label="Password"
                fullWidth
                placeholder="Enter New Password"
                type={showPassword ? 'text' : 'password'}
                value={newPass}
                error={error}
                helperText={error}
                onChange={handleNewPasswordChange}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}>
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>
          </div>
          <div className="mb-3">
            <FormControl size="small" className="w-100">
              <TextField
                autoComplete="hidden"
                size="small"
                required
                variant="outlined"
                label="Confirm Password"
                fullWidth
                placeholder="Repeat New Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmNewPass}
                onChange={handleConfirmPasswordChange}
                error={repeatPassErr}
                helperText={repeatPassErr}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowConfirmPassword}
                        onMouseDown={handleMouseDownPassword}>
                        {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>
          </div>
          <div className={`${classes.action} ${classes.content} d-flex justify-content-between`}>
            <Button
              variant="outlined"
              autoFocus
              size="large"
              onClick={redirectToLogin}
              className="my-2">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              className="btn my-2"
              onClick={resetPassword}
              disabled={disabled}>
              Continue
            </Button>
          </div>
          {loading && <CircularProgress style={{ marginLeft: 8 }} />}
        </div>
      </>
    );
  };

  if (goToLogin) return <Redirect to="/" />;
  else if (goToHome) return <Redirect to="/home" />;

  return (
    <div className={classes.pageWrap}>
      <Card className={classes.card}>
        <div className={classes.branding}>
          <img src={IconRms} alt="MOBILE icon" />
        </div>
        <div className={classes.branding2}>
          <img src={ptsLogo} alt="PTS logo" />
        </div>
        <CardContent>
          {errors.length > 0 && (
            <Alert severity="error" className={classes.alert}>
              <AlertTitle>Password validation errors</AlertTitle>
              <ul>
                {errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </Alert>
          )}

          {renderPasswordRestForm()}

          {/* <p style={{ fontWeight: 'bold' }}> v. {appVersion}</p> */}
          <div className={classes.version}> v. {appVersion}</div>
        </CardContent>
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({
  themeMode: state.theme.mode,
  appInfo: state.appInfo,
  websocket: state.websocket,
});

export default connect(mapStateToProps, {
  notify,
  submitLoginForm,
})(PageResetPassword);
